import type { AxiosError, GenericAbortSignal } from 'axios';
import axios from 'axios';

import i18next from '@/i18n';
import { ROUTES } from '@/shared/routes';
import { getAppNavigate } from '@/shared/services/helpers/navigate-helper';
import { isJwtExpiring } from '@/shared/services/jwt';
import {
  getUserToken,
  removeUserToken,
  setUserToken
} from '@/shared/services/user-hash';
import { store } from '@/store';
import { setIsAuthorized } from '@/store/auth.slice';

import type { IAuthTokens } from './api.types/auth.api-types';

export type TCustomAxiosError = AxiosError<{
  message?: string;
  statusCode: number;
  error?: {
    message: string;
    details?: unknown;
  };
}>;

/** За сколько секунд до истечения access-токена обновлять его заранее. */
const TOKEN_REFRESH_MARGIN_SECONDS = 30;

export class ApiConnector {
  private refreshPromise: Promise<IAuthTokens | null> | null = null;

  async call<Request, Response>({
    path,
    method = 'get',
    body,
    params,
    abortSignal,
    skipAuthRedirect,
    extraHeaders,
    retried = false
  }: {
    path: string;
    method?: 'get' | 'post' | 'put' | 'patch' | 'delete';
    body?: Request;
    params?: Request;
    abortSignal?: GenericAbortSignal | undefined;
    /**
     * Не трактовать 401 как «сессия истекла» (без сброса токена и редиректа на /login).
     * Для auth-эндпоинтов (login/register/otp): там 401 = неверные креды, а не протухшая
     * сессия — редирект перемонтировал бы форму и стёр введённые данные.
     */
    skipAuthRedirect?: boolean;
    /** Доп. заголовки поверх стандартных (Content-Type/Authorization) — напр. отдельный токен для PreApproval. */
    extraHeaders?: Record<string, string>;
    retried?: boolean;
  }): Promise<Response> {
    const clonedParams = structuredClone(params);
    for (const param in clonedParams) {
      if (clonedParams[param] === undefined || !String(clonedParams[param]))
        delete clonedParams[param];
    }

    try {
      const user = getUserToken();
      const headers: Record<string, string> = {};
      const bearer = user?.accessToken;
      if (bearer) headers.Authorization = `Bearer ${bearer}`;
      if (extraHeaders) Object.assign(headers, extraHeaders);

      const { data } = await axios(
        `${process.env.PUBLIC_API_BASE_URL}/` + path,
        {
          method,
          data: body,
          params: clonedParams,
          headers,
          signal: abortSignal
        }
      );
      return data;
    } catch (e: unknown) {
      const error = e as TCustomAxiosError;

      if (error?.response?.status === 401 && !skipAuthRedirect) {
        if (!retried) {
          try {
            const tokens = await this.refresh();
            if (tokens) {
              return this.call({
                path,
                method,
                body,
                params,
                abortSignal,
                skipAuthRedirect,
                extraHeaders,
                retried: true
              });
            }
          } catch (err) {
            console.error(err);
          }
        }

        this.expireSession();
      }

      const statusCode =
        error?.response?.data?.statusCode || error?.response?.status;
      let message = i18next.t('errors.generic');
      if (statusCode && statusCode !== 404 && statusCode < 500) {
        message =
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          message;
      }

      throw {
        message,
        statusCode,
        details: error?.response?.data?.error?.details,
        isCancelled: axios.isCancel(e)
      };
    }
  }

  /** Сессии больше нет: забыть токены и увести на вход, запомнив текущий адрес. */
  private expireSession(): void {
    const navigate = getAppNavigate();
    removeUserToken();
    store.dispatch(setIsAuthorized(false));

    const newPath = `/${ROUTES.login}`;
    const savedUrl =
      window.location.pathname !== `/${i18next.language}${newPath}` &&
      `${window.location.pathname}${window.location.search}`;
    navigate(newPath, { state: { savedUrl } });
  }

  /**
   * Обновляет истекающий access-токен до запроса. Нужно эндпоинтам под сессией, где 401
   * может означать неверные данные (код, пароль): они идут со `skipAuthRedirect`, и `call`
   * сам токен не обновит, а повтор запроса сжёг бы попытку. Сессия мертва — выход на вход.
   */
  protected async ensureFreshSession(): Promise<void> {
    const { accessToken } = getUserToken();
    if (!isJwtExpiring(accessToken, TOKEN_REFRESH_MARGIN_SECONDS)) return;
    if (await this.refresh()) return;

    this.expireSession();
    throw {
      message: i18next.t('errors.generic'),
      statusCode: 401,
      isCancelled: false
    };
  }

  /**
   * `POST /auth/refresh` — ротация refresh-токена; сохраняет новую пару.
   * Напрямую через axios (без `this.call`), чтобы 401 не вызвал редирект-на-логин.
   */
  async refresh(): Promise<IAuthTokens | null> {
    if (this.refreshPromise) return await this.refreshPromise;
    this.refreshPromise = new Promise((resolve) => {
      const { refreshToken } = getUserToken();
      if (!refreshToken) {
        resolve(null);
        return;
      }

      axios
        .post<IAuthTokens>(
          `${process.env.PUBLIC_API_BASE_URL}/auth/refresh`,
          { refreshToken },
          { headers: { 'Content-Type': 'application/json' } }
        )
        .then(({ data }) => {
          setUserToken({
            accessToken: data.accessToken,
            refreshToken: data.refreshToken
          });
          resolve(data);
        })
        .catch(() => {
          resolve(null);
        })
        .finally(() => {
          this.refreshPromise = null;
        });
    });
    return await this.refreshPromise;
  }
}

export const apiConnector = new ApiConnector();
